export default function PrivacyPolicyContent() {
  return (
    <>
      <h1 className="text-3xl font-bold text-white mb-2">Privacy Policy</h1>
      <p className="text-sm text-gray-400 mb-8">Last updated: June 16, 2026</p>

      <div className="space-y-8 text-gray-300 text-sm leading-relaxed">

        <section>
          <h2 className="text-lg font-semibold text-white mb-3">1. Introduction</h2>
          <p>
            Primeslink ("we," "us," or "our") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website (<span className="text-primary-400">primeslink.com</span>) or use our AI automation services. Please read this policy carefully. If you disagree with its terms, please discontinue use of our site.
          </p>
        </section>

        <section>
          <h2 className="text-lg font-semibold text-white mb-3">2. Information We Collect</h2>
          <p className="mb-3">We may collect the following types of information:</p>
          <ul className="list-disc list-inside space-y-2 pl-2">
            <li><span className="text-white font-medium">Contact Information:</span> Name, email address, phone number, and company name submitted through our contact forms or AI chat.</li>
            <li><span className="text-white font-medium">Business Information:</span> Business type, automation needs, and other details you share during consultations or via the chat widget.</li>
            <li><span className="text-white font-medium">Chat Interactions:</span> Messages exchanged with our AI assistant are processed to provide responses and may be stored to improve service quality.</li>
            <li><span className="text-white font-medium">Usage Data:</span> Pages visited, time spent, browser type, IP address, and referring URLs collected automatically via cookies and analytics tools.</li>
            <li><span className="text-white font-medium">Booking Data:</span> Appointment details submitted through our Calendly scheduling integration.</li>
          </ul>
        </section>

        <section>
          <h2 className="text-lg font-semibold text-white mb-3">3. How We Use Your Information</h2>
          <ul className="list-disc list-inside space-y-2 pl-2">
            <li>To respond to your inquiries and provide consultation services</li>
            <li>To schedule and manage appointments</li>
            <li>To operate and improve our AI chat assistant</li>
            <li>To send follow-up communications related to your inquiry (you may opt out at any time)</li>
            <li>To analyze website usage and improve user experience</li>
            <li>To comply with legal obligations</li>
          </ul>
        </section>

        <section>
          <h2 className="text-lg font-semibold text-white mb-3">4. AI-Powered Features</h2>
          <p className="mb-3">
            Our website uses an AI chat assistant powered by Anthropic's Claude API. When you interact with this assistant:
          </p>
          <ul className="list-disc list-inside space-y-2 pl-2">
            <li>Your messages are transmitted to Anthropic's servers to generate responses.</li>
            <li>AI responses are generated algorithmically and may occasionally be inaccurate or incomplete.</li>
            <li>The chat assistant is not a human — it is an AI system.</li>
            <li>Lead information you share (name, email, phone) may be stored in our database to follow up on your inquiry.</li>
          </ul>
        </section>

        <section>
          <h2 className="text-lg font-semibold text-white mb-3">5. Cookies & Tracking Technologies</h2>
          <p className="mb-3">We use the following types of cookies:</p>
          <ul className="list-disc list-inside space-y-2 pl-2">
            <li><span className="text-white font-medium">Necessary Cookies:</span> Required for the website to function. Cannot be disabled.</li>
            <li><span className="text-white font-medium">Analytics Cookies:</span> Help us understand how visitors interact with our website (e.g., Google Analytics). Only set with your consent.</li>
            <li><span className="text-white font-medium">Marketing Cookies:</span> Used to deliver relevant advertisements and track campaign effectiveness. Only set with your consent.</li>
          </ul>
          <p className="mt-3">You can manage your cookie preferences at any time using the Cookie Settings option in our website footer.</p>
        </section>

        <section>
          <h2 className="text-lg font-semibold text-white mb-3">6. Third-Party Services</h2>
          <p className="mb-3">We share information with the following third-party providers to operate our services:</p>
          <ul className="list-disc list-inside space-y-2 pl-2">
            <li><span className="text-white font-medium">Anthropic:</span> Powers our AI chat assistant (anthropic.com)</li>
            <li><span className="text-white font-medium">Supabase:</span> Secure database storage for lead information</li>
            <li><span className="text-white font-medium">Calendly:</span> Appointment scheduling</li>
            <li><span className="text-white font-medium">EmailJS:</span> Contact form email delivery</li>
          </ul>
          <p className="mt-3 font-medium text-white">We do not sell your personal information to any third party.</p>
        </section>

        <section>
          <h2 className="text-lg font-semibold text-white mb-3">7. Data Retention</h2>
          <p>
            We retain your personal information for as long as necessary to fulfill the purposes described in this policy, or as required by law. Lead and contact data is retained for up to 2 years from the date of last contact. You may request deletion of your data at any time.
          </p>
        </section>

        <section>
          <h2 className="text-lg font-semibold text-white mb-3">8. Your Rights</h2>
          <p className="mb-3">Depending on your location, you may have the right to:</p>
          <ul className="list-disc list-inside space-y-2 pl-2">
            <li>Access the personal data we hold about you</li>
            <li>Request correction of inaccurate data</li>
            <li>Request deletion of your personal data</li>
            <li>Opt out of marketing communications</li>
            <li>Withdraw consent for non-essential cookies</li>
          </ul>
          <p className="mt-3">To exercise any of these rights, contact us at <a href="mailto:info@primeslink.com" className="text-primary-400 hover:underline">info@primeslink.com</a>.</p>
        </section>

        <section>
          <h2 className="text-lg font-semibold text-white mb-3">9. Data Security</h2>
          <p>
            We implement appropriate technical and organizational measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. However, no method of transmission over the internet is 100% secure, and we cannot guarantee absolute security.
          </p>
        </section>

        <section>
          <h2 className="text-lg font-semibold text-white mb-3">10. Children's Privacy</h2>
          <p>
            Our services are not directed to individuals under the age of 16. We do not knowingly collect personal information from children. If you believe a child has provided us with personal data, please contact us immediately.
          </p>
        </section>

        <section>
          <h2 className="text-lg font-semibold text-white mb-3">11. Changes to This Policy</h2>
          <p>
            We may update this Privacy Policy from time to time. We will notify you of significant changes by updating the "Last updated" date at the top of this page. Your continued use of our services after changes are posted constitutes your acceptance of the revised policy.
          </p>
        </section>

        <section>
          <h2 className="text-lg font-semibold text-white mb-3">12. Contact Us</h2>
          <p>If you have questions or concerns about this Privacy Policy, please contact us:</p>
          <div className="mt-3 p-4 bg-white/5 rounded-lg border border-white/10 space-y-1">
            <p className="text-white font-medium">Primeslink</p>
            <p>Email: <a href="mailto:info@primeslink.com" className="text-primary-400 hover:underline">info@primeslink.com</a></p>
            <p>Phone: <a href="tel:+19145255267" className="text-primary-400 hover:underline">+1 914 525 5267</a></p>
          </div>
        </section>
      </div>
    </>
  );
}
